import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import GymMinimalBrandingFields from './GymMinimalBrandingFields';
import PhoneField from '../ui/PhoneField';
import { resolveOrganizationBrandName } from '../../utils/branding';

const emptyForm = {
  nombre: '',
  brand_name: '',
  ciudad: '',
  email: '',
  telefono: '',
  logo_url: '',
  welcome_message: '',
  support_whatsapp: '',
  primary_color: '#2563eb',
  secondary_color: '#10b981',
  status: 'active',
};

export default function EditHoldingVirtualGymModal({ gym, open, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !gym) return;
    setError('');
    setForm({
      nombre: gym.nombre || '',
      brand_name: gym.brand_name || gym.nombre || '',
      ciudad: gym.ciudad || '',
      email: gym.email || '',
      telefono: gym.telefono || gym.teléfono || '',
      logo_url: gym.logo_url || '',
      welcome_message: gym.welcome_message || '',
      support_whatsapp: gym.support_whatsapp || '',
      primary_color: gym.primary_color || '#2563eb',
      secondary_color: gym.secondary_color || '#10b981',
      status: gym.status === 'inactive' || gym.activo === false ? 'inactive' : 'active',
    });
  }, [open, gym]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, saving]);

  if (!open || !gym) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.put(`/gyms/${gym.id}`, {
        nombre: form.nombre.trim(),
        brand_name: form.brand_name?.trim() || form.nombre.trim(),
        ciudad: form.ciudad?.trim() || null,
        email: form.email?.trim() || null,
        telefono: form.telefono?.trim() || null,
        logo_url: form.logo_url?.trim() || undefined,
        welcome_message: form.welcome_message?.trim() || undefined,
        support_whatsapp: form.support_whatsapp?.trim() || undefined,
        primary_color: form.primary_color || '#2563eb',
        secondary_color: form.secondary_color || '#10b981',
        status: form.status,
      });
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || t('holding.edit_error', 'No se pudo actualizar el gimnasio'));
    } finally {
      setSaving(false);
    }
  };

  const displayName = resolveOrganizationBrandName({ entity: gym }) || gym.nombre;

  return (
    <div className="form-modal-overlay" style={{ zIndex: 9998 }} role="dialog" aria-modal="true" onClick={() => !saving && onClose?.()}>
      <div
        className="form-modal d28d-admin-modal ui-light-panel surface-readable bg-white border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="form-modal-header">
          <div className="min-w-0">
            <h3 className="font-bold text-stone-900 truncate">
              {t('holding.edit_gym', 'Editar gimnasio virtual')}
            </h3>
            <p className="text-xs text-stone-500 mt-0.5 truncate">{displayName}</p>
          </div>
          <button type="button" className="form-close-btn shrink-0" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <form className="form-modal-content space-y-4" onSubmit={handleSubmit}>
          {error ? <div className="alert-error text-sm">{error}</div> : null}
          {gym.admin_email ? (
            <p className="text-xs text-stone-500 rounded-lg bg-stone-50 border border-stone-200 px-3 py-2">
              {t('holding.operator', 'Operador')}: <span className="font-medium text-stone-700">{gym.admin_email}</span>
            </p>
          ) : null}
          <label className="block text-sm text-stone-800">
            <span className="font-semibold">{t('tenants.field_name', 'Nombre del gimnasio virtual')}</span>
            <input className="input mt-1 w-full" name="nombre" required value={form.nombre} onChange={handleChange} />
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block text-sm text-stone-800">
              <span className="font-semibold">{t('tenants.field_city', 'Ciudad')}</span>
              <input className="input mt-1 w-full" name="ciudad" value={form.ciudad} onChange={handleChange} />
            </label>
            <label className="block text-sm text-stone-800">
              <span className="font-semibold">{t('tenants.field_contact_email', 'Email de contacto')}</span>
              <input className="input mt-1 w-full" type="email" name="email" value={form.email} onChange={handleChange} />
            </label>
          </div>
          <PhoneField
            label={t('holding.field_phone', 'Teléfono')}
            value={form.telefono}
            onChange={(v) => setForm((prev) => ({ ...prev, telefono: v }))}
            inputClassName="input mt-1 w-full"
            className="block text-sm text-stone-800"
            labelClassName="font-semibold"
          />
          <label className="block text-sm text-stone-800">
            <span className="font-semibold">{t('holding.field_status', 'Estado')}</span>
            <select className="input mt-1 w-full" name="status" value={form.status} onChange={handleChange}>
              <option value="active">{t('holding.status_active', 'Activo')}</option>
              <option value="inactive">{t('holding.status_inactive', 'Inactivo')}</option>
            </select>
            <span className="text-xs text-stone-500 mt-0.5 block">
              {t('holding.status_hint', 'Un gimnasio inactivo no permite nuevos accesos ni operación normal.')}
            </span>
          </label>
          <GymMinimalBrandingFields formData={form} setFormData={setForm} showBrandName />
          <div className="flex gap-2 pt-2">
            <button type="button" className="btn-secondary flex-1" onClick={onClose} disabled={saving}>
              {t('common.cancel', 'Cancelar')}
            </button>
            <button type="submit" className="btn-primary flex-1" disabled={saving}>
              {saving ? t('common.saving', 'Guardando…') : t('common.save', 'Guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
